import { useState, useRef, useEffect, KeyboardEvent } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";

interface User {
  id: string;
  full_name: string | null;
  avatar_url: string | null;
}

interface MentionInputProps {
  value: string;
  onChange: (value: string, mentions: string[]) => void;
  onSubmit?: () => void;
  placeholder?: string;
  workspaceId?: string;
  disabled?: boolean;
  className?: string;
  rows?: number;
}

export const MentionInput = ({
  value,
  onChange,
  onSubmit,
  placeholder = "Add a comment... Use @ to mention someone",
  workspaceId,
  disabled = false,
  className,
  rows = 2,
}: MentionInputProps) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState<User[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [mentionSearch, setMentionSearch] = useState("");
  const [mentionStart, setMentionStart] = useState(-1);
  const [mentions, setMentions] = useState<string[]>([]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (mentionSearch && workspaceId) {
      fetchUsers();
    } else {
      setSuggestions([]);
    }
  }, [mentionSearch, workspaceId]);

  const fetchUsers = async () => {
    try {
      // Get workspace members
      const { data: members, error } = await supabase
        .from("workspace_members")
        .select(`
          user_id,
          profiles:user_id (
            id,
            full_name,
            avatar_url
          )
        `)
        .eq("workspace_id", workspaceId)
        .limit(10);

      if (error) throw error;

      const users = (members || [])
        .map((m: any) => m.profiles)
        .filter((p: any) => p && (
          p.full_name?.toLowerCase().includes(mentionSearch.toLowerCase()) ||
          p.id.includes(mentionSearch)
        ));

      setSuggestions(users);
      setSelectedIndex(0);
    } catch (error) {
      console.error("Failed to fetch users:", error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    const cursorPos = e.target.selectionStart || 0;

    // Check if we're typing a mention
    const textBeforeCursor = newValue.slice(0, cursorPos);
    const lastAtIndex = textBeforeCursor.lastIndexOf("@");

    if (lastAtIndex !== -1) {
      const textAfterAt = textBeforeCursor.slice(lastAtIndex + 1);
      // Check if there's no space after @ (still typing the mention)
      if (!textAfterAt.includes(" ") && !textAfterAt.includes("\n")) {
        setMentionStart(lastAtIndex);
        setMentionSearch(textAfterAt);
        setShowSuggestions(true);
      } else {
        setShowSuggestions(false);
        setMentionSearch("");
      }
    } else {
      setShowSuggestions(false);
      setMentionSearch("");
    }

    onChange(newValue, mentions);
  };

  const insertMention = (user: User) => {
    if (!textareaRef.current || mentionStart === -1) return;

    const beforeMention = value.slice(0, mentionStart);
    const cursorPos = textareaRef.current.selectionStart || 0;
    const afterCursor = value.slice(cursorPos);

    const displayName = user.full_name || `User ${user.id.slice(0, 8)}`;
    const newValue = `${beforeMention}@${displayName} ${afterCursor}`;

    const newMentions = [...mentions, user.id];
    setMentions(newMentions);
    onChange(newValue, newMentions);

    setShowSuggestions(false);
    setMentionSearch("");
    setMentionStart(-1);

    // Focus and move cursor after mention
    setTimeout(() => {
      if (textareaRef.current) {
        const newCursorPos = beforeMention.length + displayName.length + 2;
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (showSuggestions && suggestions.length > 0) {
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev < suggestions.length - 1 ? prev + 1 : 0
          );
          break;
        case "ArrowUp":
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev > 0 ? prev - 1 : suggestions.length - 1
          );
          break;
        case "Enter":
          e.preventDefault();
          insertMention(suggestions[selectedIndex]);
          break;
        case "Escape":
          e.preventDefault();
          setShowSuggestions(false);
          break;
        case "Tab":
          e.preventDefault();
          insertMention(suggestions[selectedIndex]);
          break;
      }
    } else if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      onSubmit?.();
    }
  };

  const getInitials = (name: string | null) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="relative">
      <Textarea
        ref={textareaRef}
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onBlur={() => {
          // Delay hiding to allow click on suggestion
          setTimeout(() => setShowSuggestions(false), 200);
        }}
        placeholder={placeholder}
        disabled={disabled}
        rows={rows}
        className={cn("resize-none", className)}
      />

      {/* Suggestions dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div
          ref={suggestionsRef}
          className="absolute z-50 mt-1 w-64 bg-popover border rounded-lg shadow-lg overflow-hidden"
        >
          {suggestions.map((user, index) => (
            <div
              key={user.id}
              className={cn(
                "flex items-center gap-2 p-2 cursor-pointer transition-colors",
                index === selectedIndex
                  ? "bg-accent text-accent-foreground"
                  : "hover:bg-muted"
              )}
              onClick={() => insertMention(user)}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              <Avatar className="h-6 w-6">
                <AvatarImage src={user.avatar_url || undefined} />
                <AvatarFallback className="text-xs">
                  {getInitials(user.full_name)}
                </AvatarFallback>
              </Avatar>
              <span className="text-sm truncate">
                {user.full_name || `User ${user.id.slice(0, 8)}`}
              </span>
            </div>
          ))}
        </div>
      )}

      {showSuggestions && suggestions.length === 0 && mentionSearch && (
        <div className="absolute z-50 mt-1 w-64 bg-popover border rounded-lg shadow-lg p-3 text-sm text-muted-foreground">
          No users found matching "{mentionSearch}"
        </div>
      )}
    </div>
  );
};
